/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * RELATIVE pluspunten (D36, Jasper 2026-10-08: "when a provider/contract has
 * the (shared) lowest commission and/or the highest sort of a compare result,
 * then these become voordelen as well"). termsVerdict (src/lib/termsVerdict.ts)
 * judges an offer on its own facts; these judge it against the offers it is
 * shown WITH, so they live here and LEAD the Voordelen list wherever a
 * comparison prints one — before the derived pluspunten and the redactie's
 * own —, in one fixed order:
 *
 *   top_earnings       the highest net payout: the top of the payout order
 *                      (the comparer's default, "Hoogste opbrengst")
 *   lowest_commission  the lowest commission, whatever the order
 *
 * (`top_rating`, the top of the Google-score order, left with the ratings on
 * 2026-10-14.)
 *
 * The looptijd order crowns nobody, and neither do the commission orders:
 * lowest first is `lowest_commission` already, highest first puts the dearest
 * on top. Ties count — every row that shares the best value earns the point —
 * and the words never say "shared".
 *
 * THE COMPARER judges a row against its result set: every row the filters
 * keep, the rows behind "Toon meer" included. The rules are those of the two
 * badges the rows already wore (ResultRow's "Hoogste opbrengst" and "Laagste
 * commissie"), and the badges read `standingsIn` too, so a badge and a point
 * can never name different rows:
 *   - payout: the highest estimate among the rows that have one, and only
 *     above € 0 — at a volume too small for one ERE every row nets € 0, and a
 *     payout of nothing is nobody's "highest";
 *   - commission: the lowest among the rows with a known commission AND a
 *     known looptijd, subscriptions (`zero_commission`) left out — their 0 %
 *     is no commission-model price.
 * A single row is the lowest and the top of its own set, as it wore both
 * badges before.
 *
 * THE COMPARE PAGES (src/lib/compareQuestions.ts) take the table's own crowns:
 * the winners of Netto per jaar earn `top_earnings`, the winners of Commissie
 * `lowest_commission`, and a row whose offers are all equal crowns nobody, so
 * it prints no point.
 *
 * Pure: no React, no i18n strings, no window. Full `offer_terms.bullet.*` keys
 * in the verdict's item shape (topic `relative`), worded and drawn by
 * DerivedBullets like every other pluspunt, in the bake and the browser alike.
 */

import type { EREProvider } from '../types';
import type { SortDirection, SortField } from './comparerSort';
import type { VerdictItem } from './termsVerdict';

/** A relative pluspunt: a derived item of the verdict's shape, under its own topic. */
export type RelativeItem = Extract<VerdictItem, { key: string }> & { topic: 'relative' };

/** The order whose top is a merit: the payout, highest first. */
export type RelativeTop = 'earnings';

/** The relative pluspunten one offer earns where it is shown — what a comparer row is handed. */
export interface RelativePluspunten {
  /** The (shared) lowest commission of the set. */
  lowestCommission: boolean;
  /**
   * The (shared) top of the active order, when that order puts a merit first;
   * null under any other order. The compare pages have no order: there it is
   * the Netto per jaar crown (`'earnings'`).
   */
  topOfSort: RelativeTop | null;
}

/** Where a comparer row stands in its result set, whatever the order. */
export interface RowStanding {
  /** The "Laagste commissie" badge, and always the `lowest_commission` pluspunt. */
  lowestCommission: boolean;
  /** The "Hoogste opbrengst" badge, and the `top_earnings` pluspunt under the payout order. */
  topEarnings: boolean;
}

export interface StandingOptions {
  /**
   * The row's estimated net payout at the comparison's volume and €/ERE — the
   * figure the list sorts by (comparerSort's `earningsOf`); null when it
   * cannot be estimated.
   */
  earningsOf: (p: EREProvider) => number | null;
  /**
   * The commission % a row is judged on for `lowest_commission` — the comparer
   * passes the one its sort compares (comparerSort's `commissionOf`:
   * commissionSortValue at its volume and price), so the crown and the order
   * agree on a staffel or a vastrecht offer. Absent: the row's `commissionFee`.
   */
  commissionOf?: (p: EREProvider) => number | null;
}

const bullet = (leaf: string) => `offer_terms.bullet.${leaf}`;

const TOP_KEY: Readonly<Record<RelativeTop, string>> = {
  earnings: bullet('top_earnings'),
};
const LOWEST_COMMISSION_KEY = bullet('lowest_commission');

/** Every key this module can emit, in the order the points are printed — the coverage tests read it. */
export const RELATIVE_BULLET_KEYS: readonly string[] = [TOP_KEY.earnings, LOWEST_COMMISSION_KEY];

/** A finite number, or null — a gap in an older payload never wins anything. */
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * The commission a row may be "lowest" with: known, beside a known looptijd,
 * never a subscription's. A staffel and a vastrecht offer are claimable like a
 * flat one, on the % the caller judges them by (`commissionOf`).
 */
const claimableCommission = (
  p: EREProvider,
  feeOf: (p: EREProvider) => number | null
): number | null =>
  p.contractDuration !== null && p.deviatingModel !== 'zero_commission' ? num(feeOf(p)) : null;

const ownCommissionFee = (p: EREProvider): number | null => p.commissionFee;

/** The lowest or highest of the known values; null when none is known. */
function extreme(values: readonly (number | null)[], dir: 'low' | 'high'): number | null {
  let best: number | null = null;
  for (const v of values) {
    if (v === null) continue;
    if (best === null || (dir === 'low' ? v < best : v > best)) best = v;
  }
  return best;
}

/**
 * The judge of a result set: every row the visitor is looking at, in any
 * order. The best values are read once; the returned function says, for a
 * row of that set, what it is best at. A set without a claimable value
 * crowns nobody on it.
 */
export function standingsIn(
  rows: readonly EREProvider[],
  { earningsOf, commissionOf = ownCommissionFee }: StandingOptions
): (row: EREProvider) => RowStanding {
  const payoutOf = (p: EREProvider): number | null => num(earningsOf(p));
  const claimable = (p: EREProvider) => claimableCommission(p, commissionOf);
  const lowestCommission = extreme(rows.map(claimable), 'low');
  const highestPayout = extreme(rows.map(payoutOf), 'high');
  // A payout of nothing is nobody's highest.
  const topPayout = highestPayout !== null && highestPayout > 0 ? highestPayout : null;
  return (row) => {
    const fee = claimable(row);
    const payout = payoutOf(row);
    return {
      lowestCommission: fee !== null && fee === lowestCommission,
      topEarnings: payout !== null && payout === topPayout,
    };
  };
}

/**
 * What a row's standing earns under the active order: the lowest commission
 * always; the top only of the order that puts a merit first (the payout,
 * highest first).
 */
export function relativePluspunten(
  standing: RowStanding,
  sortField: SortField,
  sortDirection: SortDirection
): RelativePluspunten {
  const topOfSort: RelativeTop | null =
    sortDirection === 'desc' && sortField === 'earnings' && standing.topEarnings
      ? 'earnings'
      : null;
  return { lowestCommission: standing.lowestCommission, topOfSort };
}

/**
 * The relative pluspunten as items, in their fixed order — the top of the
 * order first, then the lowest commission; none without any.
 */
export function relativeBulletItems(
  relative: RelativePluspunten | null | undefined
): RelativeItem[] {
  if (!relative) return [];
  const items: RelativeItem[] = [];
  if (relative.topOfSort) items.push({ topic: 'relative', key: TOP_KEY[relative.topOfSort] });
  if (relative.lowestCommission) items.push({ topic: 'relative', key: LOWEST_COMMISSION_KEY });
  return items;
}
